import { CanvasMode } from '../../types';

/**
 * KeyboardService
 * 
 * 职责：
 * 1. 管理应用程序的所有键盘快捷键
 * 2. 提供注册和处理快捷键事件的机制
 * 3. 支持组合键和自定义快捷键配置
 * 4. 处理各种模式下的快捷键冲突
 */
export class KeyboardService {
  // 快捷键映射，格式为 { [快捷键]: 回调函数 }
  private shortcuts: Map<string, () => void> = new Map();
  
  // 是否启用快捷键
  private enabled: boolean = true;
  
  constructor() {
    // 监听全局键盘事件
    document.addEventListener('keydown', this.handleKeyDown);
  }
  
  /**
   * 注册快捷键
   * @param key 快捷键字符串，格式如 "ctrl+z" 或 "shift+p"
   * @param callback 快捷键触发时的回调函数
   */
  registerShortcut(key: string, callback: () => void): void {
    this.shortcuts.set(key.toLowerCase(), callback);
  }
  
  /**
   * 注册批量快捷键
   * @param shortcuts 快捷键映射对象 { [快捷键]: 回调函数 }
   */
  registerShortcuts(shortcuts: Record<string, () => void>): void {
    Object.entries(shortcuts).forEach(([key, callback]) => {
      this.registerShortcut(key, callback);
    });
  }
  
  /**
   * 注册工具快捷键
   * @param key 快捷键字符串
   * @param selectToolCallback 选择工具的回调函数
   */
  registerToolShortcut(
    key: string, 
    selectToolCallback: () => void, 
    _lockToolCallback: () => void // 保留参数以兼容现有代码，但不再使用
  ): void {
    this.registerShortcut(key, selectToolCallback);
  }
  
  /**
   * 启用快捷键
   */
  enable(): void {
    this.enabled = true;
  }
  
  /**
   * 禁用快捷键
   */
  disable(): void {
    this.enabled = false;
  }
  
  /**
   * 处理键盘事件
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    if (!this.enabled) return;
    
    // 在输入框内不触发快捷键
    if (
      event.target instanceof HTMLInputElement || 
      event.target instanceof HTMLTextAreaElement ||
      (event.target as HTMLElement).isContentEditable
    ) {
      return;
    }
    
    // 构建快捷键字符串
    const key = this.buildKeyString(event);
    
    // 查找并执行对应的回调函数
    const callback = this.shortcuts.get(key);
    if (callback) {
      event.preventDefault();
      callback();
    }
  };
  
  /**
   * 根据键盘事件构建快捷键字符串
   */
  private buildKeyString(event: KeyboardEvent): string {
    const parts: string[] = [];
    
    if (event.ctrlKey) parts.push('ctrl');
    if (event.altKey) parts.push('alt');
    if (event.shiftKey) parts.push('shift');
    if (event.metaKey) parts.push('meta');
    
    // 特殊按键处理
    if (event.key === ' ') {
      parts.push('space');
    } else if (event.key === 'Escape') {
      parts.push('esc');
    } else if (event.key === 'Delete') {
      parts.push('delete');
    } else if (event.key === 'Backspace') {
      parts.push('backspace');
    } else if (event.key.length === 1) {
      // 普通按键直接添加小写形式
      parts.push(event.key.toLowerCase());
    } else {
      // 其他特殊按键直接添加
      parts.push(event.key.toLowerCase());
    }
    
    return parts.join('+');
  }
  
  /**
   * 销毁服务，移除监听器
   */
  destroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
  }
}

// 默认快捷键配置
export const DEFAULT_SHORTCUTS = {
  // 常用工具快捷键
  'v': CanvasMode.SELECT,    // 选择工具
  'p': CanvasMode.DRAW,      // 画笔工具
  's': CanvasMode.SHAPE,     // 形状工具
  't': CanvasMode.TEXT,      // 文本工具
  
  // 编辑操作快捷键
  'ctrl+z': 'undo',          // 撤销
  'ctrl+shift+z': 'redo',    // 重做
  'ctrl+y': 'redo',          // 重做（替代方式）
  'ctrl+c': 'copy',          // 复制
  'ctrl+v': 'paste',         // 粘贴
  'ctrl+x': 'cut',           // 剪切
  'ctrl+a': 'selectAll',     // 全选
  'delete': 'delete',        // 删除
  'backspace': 'delete',     // 删除（替代方式）
  'ctrl+d': 'duplicate',     // 复制所选
  
  // 视图操作快捷键
  'ctrl+0': 'resetZoom',     // 重置缩放
  'ctrl++': 'zoomIn',        // 放大
  'ctrl+-': 'zoomOut',       // 缩小
  'space': 'tempHandTool',   // 临时手型工具
  
  // 文件操作快捷键
  'ctrl+s': 'save',          // 保存
  'ctrl+n': 'new',           // 新建
  'ctrl+o': 'open',          // 打开
  
  // 其他快捷键
  'esc': 'cancelOperation',  // 取消当前操作
  'ctrl+/': 'toggleHelp'     // 切换帮助面板
}; 